import React from 'react';
import { useApp } from '../../context/AppContext';
import {
  Sun,
  Moon,
  Settings,
} from 'lucide-react';
import { Logo } from '../common/Logo';

export const Navbar: React.FC = () => {
  const {
    theme,
    setTheme,
    currentPage,
    setCurrentPage,
  } = useApp();

  const isDark =
    theme === 'dark' ||
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  const isSettingsActive = currentPage === 'settings';

  return (
    <header className="sticky top-0 z-30 bg-gradient-to-r from-emerald-800 via-teal-800 to-emerald-900 dark:from-[#062c21] dark:via-[#0a3028] dark:to-[#041e17] border-b border-emerald-700/40 dark:border-emerald-500/20 shadow-lg shadow-emerald-950/15 transition-all relative overflow-hidden">
      {/* Subtle ambient light glow across the navbar */}
      <div className="absolute top-0 left-1/4 w-96 h-12 bg-white/5 rounded-full blur-xl pointer-events-none" />
      <div className="absolute -bottom-6 right-10 w-48 h-12 bg-teal-400/10 rounded-full blur-xl pointer-events-none" />

      <div className="w-full px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4 relative z-10">
        {/* Left: Brand Logo & Title with lightText enabled */}
        <div className="cursor-pointer transition-transform duration-150 active:scale-98" onClick={() => setCurrentPage('dashboard')}>
          <Logo size="md" showSubtitle={true} lightText={true} />
        </div>

        {/* Right Controls: Theme Toggle & Settings */}
        <div className="flex items-center gap-2">
          {/* Theme Toggle (Dark/Light) */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle Theme"
            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            className="p-2 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 active:bg-white/30 border border-white/15 rounded-xl transition-all shadow-xs backdrop-blur-xs active:scale-95"
          >
            {isDark ? (
              <Sun className="w-4 h-4 text-amber-300" />
            ) : (
              <Moon className="w-4 h-4 text-teal-200" />
            )}
          </button>

          {/* Settings Button */}
          <button
            onClick={() => setCurrentPage('settings')}
            aria-label="Settings"
            title="Settings & Security"
            className={`p-2 rounded-xl transition-all shadow-xs backdrop-blur-xs active:scale-95 text-white ${
              isSettingsActive
                ? 'bg-white/25 border border-white/40 font-bold shadow-md shadow-emerald-950/20'
                : 'bg-white/10 hover:bg-white/20 active:bg-white/30 border border-white/15 text-white/90 hover:text-white'
            }`}
          >
            <Settings className={`w-4 h-4 ${isSettingsActive ? 'stroke-[2.5]' : ''}`} />
          </button>
        </div>
      </div>
    </header>
  );
};
